import { useState, useEffect } from 'react';

/**
 * Custom hook for reactive theme detection
 * Listens to changes in document.documentElement classList
 * Returns true if dark mode is active, false if light mode
 * 
 * Features:
 * - SSR-safe with proper fallback handling
 * - Lazy initialization to avoid hydration mismatch
 * - MutationObserver for instant reactivity
 * - Auto-sync on mount to handle initial state
 */
export const useTheme = (): boolean => {
  // Lazy initialization - function only runs once during initial render
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    // SSR safety check
    if (typeof document === 'undefined') {
      return true; // Default to dark mode during SSR
    }
    // Check actual DOM state
    return !document.documentElement.classList.contains('light');
  });

  useEffect(() => {
    // Additional SSR safety check
    if (typeof document === 'undefined') return;

    // Helper function to update theme state
    const updateTheme = () => {
      const isLight = document.documentElement.classList.contains('light');
      setIsDarkMode(!isLight);
    };

    // Sync immediately on mount to ensure correct initial state
    updateTheme();

    // MutationObserver to watch for class changes on <html> element
    const observer = new MutationObserver(updateTheme);

    // Observe the documentElement for attribute changes (specifically 'class')
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    // Cleanup observer on unmount
    return () => {
      observer.disconnect();
    };
  }, []);

  return isDarkMode;
};
